// src/App.jsx
import React, { useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import { useMasterData } from './context/MasterDataContext';

import AppLayout          from './components/Layout/AppLayout';
import Login              from './pages/Login/Login';
import Dashboard          from './pages/Dashboard/Dashboard';
import EmployeeList        from './pages/Employees/EmployeeList';
import EmployeeDetail      from './pages/Employees/EmployeeDetail';
import AddEmployee         from './pages/AddEmployee/AddEmployee';
import BulkUpload          from './pages/BulkUpload/BulkUpload';
import CohortDashboard     from './pages/Cohort/CohortDashboard';
import AttritionDashboard  from './pages/Attrition/AttritionDashboard';
import AttritionUpload     from './pages/Attrition/AttritionUpload';
import CompliancePage      from './pages/Compliance/CompliancePage';
import CallBulkUpload      from './pages/CallBulkUpload/CallBulkUpload';
import CohortConfigPage    from './pages/Admin/CohortConfigPage';
import SystemLogPage          from './pages/Admin/SystemLogPage';
import UserManagementPage     from './pages/Admin/UserManagementPage';
import InboundPage         from './pages/Inbound/InboundPage';
import CampusPage          from './pages/Campus/CampusPage';
import AttritionOccupancyDashboard from './pages/AttritionOccupancy/AttritionOccupancyDashboard';

// ── Eris Connect brand tokens (unchanged) ────────────────────────
const erisTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary:         '#1E6FBF',
    colorLink:            '#1E6FBF',
    colorSuccess:         '#15803d',
    colorWarning:         '#b45309',
    colorError:           '#b91c1c',
    colorInfo:            '#1E6FBF',
    colorTextBase:        '#1a2d4a',
    colorBgBase:          '#ffffff',
    colorBgContainer:     '#ffffff',
    colorBgLayout:        '#f0f4f8',
    colorBorder:          '#dce6f0',
    colorBorderSecondary: '#eaf1f8',
    borderRadius:          8,
    borderRadiusLG:        12,
    borderRadiusSM:        6,
    fontFamily:           "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize:              14,
    fontSizeLG:            16,
    lineHeight:            1.6,
    controlHeight:         40,
    controlHeightLG:       44,
    controlHeightSM:       32,
    boxShadow:            '0 2px 8px rgba(27,45,107,0.08)',
    boxShadowSecondary:   '0 4px 16px rgba(27,45,107,0.12)',
    motion:                true,
  },
  components: {
    Layout: {
      siderBg:      '#1B2D6B',
      headerBg:     '#ffffff',
      bodyBg:       '#f0f4f8',
      triggerBg:    '#152358',
      triggerColor: '#ffffff',
    },
    Menu: {
      darkItemBg:            '#1B2D6B',
      darkSubMenuItemBg:     '#152358',
      darkItemColor:         'rgba(255,255,255,0.65)',
      darkItemHoverColor:    '#ffffff',
      darkItemSelectedColor: '#ffffff',
      darkItemSelectedBg:    '#1E6FBF',
      darkItemHoverBg:       'rgba(255,255,255,0.08)',
    },
    Button: {
      colorPrimary:       '#1B2D6B',
      colorPrimaryHover:  '#1E6FBF',
      colorPrimaryActive: '#152358',
      primaryShadow:      'none',
      defaultShadow:      'none',
    },
    Table: {
      headerBg:           '#eaf1f8',
      headerColor:        '#1B2D6B',
      headerSortActiveBg: '#dce6f0',
      rowHoverBg:         '#f5f8fc',
      borderColor:        '#dce6f0',
    },
    Card: {
      colorBorderSecondary: '#dce6f0',
      boxShadow:            '0 2px 8px rgba(27,45,107,0.06)',
    },
    Input: {
      activeBorderColor: '#1E6FBF',
      hoverBorderColor:  '#1E6FBF',
      activeShadow:      '0 0 0 3px rgba(30,111,191,0.1)',
    },
    Select: {
      optionSelectedBg:    '#eaf1f8',
      optionSelectedColor: '#1B2D6B',
    },
    Tag: {
      defaultBg:    '#eaf1f8',
      defaultColor: '#1B2D6B',
    },
    Badge: {
      colorBgContainer: '#ffffff',
    },
    Statistic: {
      titleFontSize:   13,
      contentFontSize: 28,
    },
  },
};

// ── Auth guard ───────────────────────────────────────────────────
function RequireAuth({ children }) {
  const token = localStorage.getItem('ejt_token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

// ── Inner app — has access to MasterDataContext ───────────────────
function AppRoutes() {
  const { loadMasterData } = useMasterData();

  // ── CASE 1: User is already logged in (page refresh / returning user)
  // Token exists in localStorage → load master data immediately on mount
  useEffect(() => {
    const token = localStorage.getItem('ejt_token');
    if (token) {
      loadMasterData();   // skips if already cached in localStorage
    }
  }, [loadMasterData]);

  // ── CASE 2: Fresh login
  // Login page calls this after storing the token
  const onLoginSuccess = useCallback(async () => {
    await loadMasterData();
  }, [loadMasterData]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login onLoginSuccess={onLoginSuccess} />} />

        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Protected */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          {/* ── Core ── */}
          <Route path="dashboard"          element={<Dashboard />} />
          <Route path="cohort"             element={<CohortDashboard />} />

          {/* ── Employees ── */}
          <Route path="employees"          element={<EmployeeList />} />
          <Route path="employees/:id"      element={<EmployeeDetail />} />
          <Route path="add"                element={<AddEmployee />} />
          <Route path="bulk-upload"        element={<BulkUpload />} />

          {/* ── Analytics ── */}
          <Route path="attrition"          element={<AttritionDashboard />} />
          <Route path="attrition/upload"   element={<AttritionUpload />} />
          <Route path="compliance"         element={<CompliancePage />} />

          {/* ── Calls ── */}
          <Route path="call-bulk-upload"   element={<CallBulkUpload />} />
          <Route path="inbound"            element={<InboundPage />} />
          <Route path="campus"             element={<CampusPage />} />

          {/* ── Attrition & Occupancy ── */}
          <Route path="attrition-occupancy" element={<AttritionOccupancyDashboard />} />

          {/* ── Admin / Config ── */}
          <Route path="admin/cohort-config" element={<CohortConfigPage />} />
          <Route path="admin/system-log"    element={<SystemLogPage />} />
          <Route path="admin/users"          element={<UserManagementPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// ── Root — MasterDataProvider is in index.js wrapping this ───────
export default function App() {
  return (
    <ConfigProvider theme={erisTheme}>
      <AppRoutes />
    </ConfigProvider>
  );
}